import {Fragment} from 'react';

import {cn} from '../classname';

import {ToolbarButton} from './ToolbarButton';
import {ToolbarButtonPopup} from './ToolbarButtonPopup';
import {ToolbarListButton} from './ToolbarListButton';
import {ToolbarUpdateOnRerender} from './ToolbarRerender';
import {type ToolbarBaseProps, ToolbarDataType, type ToolbarGroupData} from './types';

import './ToolbarGroup.scss';

const b = cn('toolbar-group');

export type {ToolbarGroupData};

export type ToolbarGroupProps<E> = ToolbarBaseProps<E> & {
    data: ToolbarGroupData<E>;
};

export function ToolbarButtonGroup<E>({
    className,
    editor,
    data,
    focus,
    onClick,
    disableTooltip,
    disablePreview,
    disableHotkey,
}: ToolbarGroupProps<E>) {
    return (
        <div className={b(null, [className])}>
            {data.map((item) => {
                if (item.type === ToolbarDataType.SingleButton) {
                    return (
                        <ToolbarButton
                            {...item}
                            key={item.id}
                            editor={editor}
                            focus={focus}
                            onClick={onClick}
                            className={b(item.type, {id: item.id}, [item.className])}
                            disableTooltip={disableTooltip}
                        />
                    );
                }

                if (item.type === ToolbarDataType.ButtonPopup) {
                    return (
                        <ToolbarButtonPopup
                            {...item}
                            key={item.id}
                            editor={editor}
                            focus={focus}
                            onClick={onClick}
                            className={b(item.type, {id: item.id}, item.className)}
                            disableTooltip={disableTooltip}
                        />
                    );
                }

                if (item.type === ToolbarDataType.ListButton) {
                    return (
                        <ToolbarListButton
                            {...item}
                            qaMenu="g-md-toolbar-menu"
                            key={item.id}
                            editor={editor}
                            focus={focus}
                            onClick={onClick}
                            className={b(item.type, {id: item.id}, [item.className])}
                            disablePreview={disablePreview}
                            disableHotkey={disableHotkey}
                            disableTooltip={disableTooltip}
                        />
                    );
                }

                if (item.type === ToolbarDataType.ReactComponent) {
                    const Component = item.component;
                    const renderFn = () => (
                        <Component
                            {...item.props}
                            editor={editor}
                            focus={focus}
                            onClick={onClick}
                            className={b(item.type, {id: item.id}, [item.className])}
                        />
                    );
                    return item.noRerenderOnUpdate ? (
                        <Fragment key={item.id}>{renderFn()}</Fragment>
                    ) : (
                        <ToolbarUpdateOnRerender key={item.id} content={renderFn} />
                    );
                }

                if (item.type === ToolbarDataType.ReactNode) {
                    return item.noRerenderOnUpdate ? (
                        <Fragment key={item.id}>{item.content}</Fragment>
                    ) : (
                        <ToolbarUpdateOnRerender key={item.id} content={() => item.content} />
                    );
                }

                if (item.type === ToolbarDataType.ReactNodeFn) {
                    return item.noRerenderOnUpdate ? (
                        <Fragment key={item.id}>{item.content(editor)}</Fragment>
                    ) : (
                        <ToolbarUpdateOnRerender
                            key={item.id}
                            content={() => item.content(editor)}
                        />
                    );
                }

                return null;
            })}
        </div>
    );
}
